/**
 * Cognito JWT token management utilities
 */
/**
 * Default token provider interface
 * This should be implemented by the consuming application
 */
let tokenProvider = null;
/**
 * Callback for when authentication fails (e.g., 401 error)
 */
let onAuthenticationFailure = null;
/**
 * Set the token provider that will be used by the API client
 */
export function setTokenProvider(provider) {
    tokenProvider = provider;
}
/**
 * Set callback to be invoked when authentication fails (e.g., 401 error)
 * This allows the frontend to handle session expiration globally
 */
export function setAuthFailureCallback(callback) {
    onAuthenticationFailure = callback;
}
/**
 * Trigger the authentication failure callback
 * Called by axios interceptor when a 401 error occurs
 */
export function triggerAuthFailure() {
    if (onAuthenticationFailure) {
        onAuthenticationFailure();
    }
}
/**
 * Get the current access token
 */
export async function getAccessToken() {
    if (!tokenProvider) {
        console.warn('No token provider set. Call setTokenProvider() before making API calls.');
        return null;
    }
    try {
        const tokens = await tokenProvider.getTokens();
        return tokens?.accessToken || null;
    }
    catch (error) {
        console.error('Failed to get access token:', error);
        return null;
    }
}
/**
 * Get the current ID token (contains user claims)
 */
export async function getIdToken() {
    if (!tokenProvider) {
        console.warn('No token provider set. Call setTokenProvider() before making API calls.');
        return null;
    }
    try {
        const tokens = await tokenProvider.getTokens();
        return tokens?.idToken || null;
    }
    catch (error) {
        console.error('Failed to get ID token:', error);
        return null;
    }
}
/**
 * Clear all tokens (logout)
 */
export async function clearTokens() {
    if (tokenProvider) {
        await tokenProvider.clearTokens();
    }
}
/**
 * Parse JWT payload without verification (for client-side use only)
 */
export function parseJwtPayload(token) {
    try {
        const base64Url = token.split('.')[1];
        if (!base64Url) {
            return null;
        }
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join(''));
        return JSON.parse(jsonPayload);
    }
    catch (error) {
        console.error('Failed to parse JWT payload:', error);
        return null;
    }
}
/**
 * Check if a JWT token is expired
 */
export function isTokenExpired(token) {
    const payload = parseJwtPayload(token);
    if (!payload || !payload.exp) {
        return true;
    }
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
}
